import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { User, Loader2, Filter, Download, X, TrendingUp, ArrowUpDown } from "lucide-react";
import { getEngagedCustomers, exportEngagedCustomerChat, exportAllEngagedCustomers, markMessagesAsRead } from "../../apis/chat";
import { formatTimestampForDisplay } from "../../utils/timestampUtils";
import { usePhoneAccount } from "../../contexts/PhoneAccountContext";
import { getSocket } from "../../utils/socket";

/**
 * EngagedCustomerList Component - Displays engaged customers with scores and filters
 */
const EngagedCustomerList = ({ searchQuery, onChatSelect, selectedChat }) => {
  const { selectedPhoneId } = usePhoneAccount();
  const [engagedCustomers, setEngagedCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    min_score: "",
    max_score: "",
    start_date: "",
    end_date: "",
  });
  const [sortBy, setSortBy] = useState("total_score"); // "total_score" or "avg_score"
  const [sortOrder, setSortOrder] = useState("desc"); // "asc" or "desc"
  const [exporting, setExporting] = useState(null);
  const [exportingAll, setExportingAll] = useState(false);

  // Fetch engaged customers
  const fetchEngagedCustomers = useCallback(async () => {
    // Don't fetch if no phoneId is selected
    if (!selectedPhoneId) {
      setEngagedCustomers([]);
      return;
    }

    try {
      setLoading(true);
      const params = {
        phone_number_id: selectedPhoneId, // Always filter by selected phoneId
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      
      if (filters.min_score) {
        params.min_score = parseInt(filters.min_score, 10);
      }
      if (filters.max_score) {
        params.max_score = parseInt(filters.max_score, 10);
      }
      if (filters.start_date) {
        params.start_date = filters.start_date;
      }
      if (filters.end_date) {
        params.end_date = filters.end_date;
      }

      const response = await getEngagedCustomers(params);
      if (response.data.success) {
        setEngagedCustomers(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching engaged customers:", error);
      setEngagedCustomers([]);
    } finally {
      setLoading(false);
    }
  }, [selectedPhoneId, filters, sortBy, sortOrder]);

  // Fetch on mount and when filters change
  useEffect(() => {
    fetchEngagedCustomers();
  }, [fetchEngagedCustomers]);

  // Listen for new messages and mark as read if chat is selected
  useEffect(() => {
    if (!selectedChat) return;

    const socket = getSocket();
    if (!socket) return;

    const handleNewMessage = (messageData) => {
      const customerPhone = messageData.direction === "inbound" 
        ? messageData.from_wa_id 
        : messageData.to_wa_id;
      
      const messagePhoneId = messageData.phone_number_id;
      
      // Check if this message is for the selected chat
      if (
        selectedChat.customer_phone === customerPhone &&
        selectedChat.phone_number_id === messagePhoneId &&
        messageData.direction === "inbound"
      ) {
        // Mark messages as read in backend (async, don't block)
        markMessagesAsRead(customerPhone, messagePhoneId)
          .then((response) => {
            if (response.data.success) {
              const markedCount = response.data.data.marked || 0;
              if (markedCount > 0) {
                console.log(`✅ Marked ${markedCount} messages as read for engaged customer chat`);
              }
              
              // Set unread count to 0 in local state
              setEngagedCustomers((prevCustomers) =>
                prevCustomers.map((c) => {
                  if (
                    c.customer_number === customerPhone &&
                    c.phone_id === messagePhoneId
                  ) {
                    return { ...c, unread_count: 0 };
                  }
                  return c;
                })
              );
            }
          })
          .catch((error) => {
            console.error("Error marking messages as read in EngagedCustomerList:", error);
          });
      }
    };

    socket.on("new_message", handleNewMessage);

    return () => {
      if (socket) {
        socket.off("new_message", handleNewMessage);
      }
    };
  }, [selectedChat]);

  // Filter by search query
  const filteredCustomers = engagedCustomers.filter(
    (customer) =>
      customer.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.customer_number?.includes(searchQuery)
  );

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      min_score: "",
      max_score: "",
      start_date: "",
      end_date: "",
    });
  };

  // Export single customer (summary format)
  const handleExport = async (customer) => {
    if (exporting === customer.customer_number) return;

    try {
      setExporting(customer.customer_number);
      
      // Create CSV with summary fields for single customer
      const csvRows = [];
      
      // Header row
      csvRows.push("Customer Name,Customer Phone,Phone ID,Total Score,Average Score,Total Interactions,Max Score,Min Score,Engagement Start Date,Last Interaction Date,Unread Count");
      
      // Data row
      const name = (customer.customer_name || customer.customer_number || "").replace(/"/g, '""');
      const phone = (customer.customer_number || "").replace(/"/g, '""');
      const phoneId = (customer.phone_id || "").replace(/"/g, '""');
      const totalScore = customer.total_score || 0;
      const avgScore = customer.avg_score?.toFixed(2) || "0.00";
      const totalInteractions = customer.total_interactions || 0;
      const maxScore = customer.max_score || 0;
      const minScore = customer.min_score || 0;
      const engagementStart = customer.engagement_started_at ? new Date(customer.engagement_started_at).toLocaleString() : "";
      const lastInteraction = customer.last_interaction_at ? new Date(customer.last_interaction_at).toLocaleString() : "";
      const unreadCount = customer.unread_count || 0;
      
      csvRows.push(`"${name}","${phone}","${phoneId}",${totalScore},${avgScore},${totalInteractions},${maxScore},${minScore},"${engagementStart}","${lastInteraction}",${unreadCount}`);

      // Download CSV
      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `engaged_customer_${customer.customer_number}_${new Date().toISOString().split("T")[0]}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting customer:", error);
      alert("Failed to export customer. Please try again.");
    } finally {
      setExporting(null);
    }
  };

  // Export all engaged customers (summary format)
  const handleExportAll = async () => {
    if (exportingAll || !selectedPhoneId) return;

    try {
      setExportingAll(true);
      
      // Use the already fetched engaged customers data
      const customersToExport = filteredCustomers.length > 0 ? filteredCustomers : engagedCustomers;
      
      if (customersToExport.length === 0) {
        alert("No engaged customers to export.");
        return;
      }
      
      // Create CSV with summary fields
      const csvRows = [];
      
      // Header row
      csvRows.push("Customer Name,Customer Phone,Phone ID,Total Score,Average Score,Total Interactions,Max Score,Min Score,Engagement Start Date,Last Interaction Date,Unread Count");
      
      // Data rows
      customersToExport.forEach((customer) => {
        const name = (customer.customer_name || customer.customer_number || "").replace(/"/g, '""');
        const phone = (customer.customer_number || "").replace(/"/g, '""');
        const phoneId = (customer.phone_id || "").replace(/"/g, '""');
        const totalScore = customer.total_score || 0;
        const avgScore = customer.avg_score?.toFixed(2) || "0.00";
        const totalInteractions = customer.total_interactions || 0;
        const maxScore = customer.max_score || 0;
        const minScore = customer.min_score || 0;
        const engagementStart = customer.engagement_started_at ? new Date(customer.engagement_started_at).toLocaleString() : "";
        const lastInteraction = customer.last_interaction_at ? new Date(customer.last_interaction_at).toLocaleString() : "";
        const unreadCount = customer.unread_count || 0;
        
        csvRows.push(`"${name}","${phone}","${phoneId}",${totalScore},${avgScore},${totalInteractions},${maxScore},${minScore},"${engagementStart}","${lastInteraction}",${unreadCount}`);
      });

      // Download CSV
      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `engaged_customers_${new Date().toISOString().split("T")[0]}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting engaged customers:", error);
      alert("Failed to export engaged customers. Please try again.");
    } finally {
      setExportingAll(false);
    }
  };

  // Handle customer select
  const handleCustomerSelect = (customer) => {
    // Set unread count to 0 in local state when chat is opened
    setEngagedCustomers((prevCustomers) =>
      prevCustomers.map((c) => {
        if (
          c.customer_number === customer.customer_number &&
          c.phone_id === customer.phone_id
        ) {
          return { ...c, unread_count: 0 };
        }
        return c;
      })
    );

    const chat = {
      id: `${customer.customer_number}_${customer.phone_id}`,
      customer_phone: customer.customer_number,
      phone_number_id: customer.phone_id,
      name: customer.customer_name || customer.customer_number,
      phone: customer.customer_number,
      lastMessage: customer.last_message?.message_text || "No messages",
      timestamp: formatTimestampForDisplay(customer.last_message?.sent_at || customer.last_interaction_at),
      unread: 0, // Set to 0 when chat is opened
      is_window_open: true,
      last_message: customer.last_message,
      last_message_at: customer.last_message?.sent_at || customer.last_interaction_at,
      engagement_score: customer.total_score,
      avg_score: customer.avg_score,
    };
    onChatSelect(chat);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Filters */}
      {showFilters && (
        <div className="p-3 border-b border-gray-200 bg-gray-50">
          <div className="space-y-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Filters</span>
              <button
                onClick={clearFilters}
                className="text-xs text-primary hover:text-primary/80"
              >
                Clear
              </button>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-600">Min Score</label>
                <input
                  type="number"
                  value={filters.min_score}
                  onChange={(e) => handleFilterChange("min_score", e.target.value)}
                  placeholder="Min"
                  className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-primary/40 focus:border-primary"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600">Max Score</label>
                <input
                  type="number"
                  value={filters.max_score}
                  onChange={(e) => handleFilterChange("max_score", e.target.value)}
                  placeholder="Max"
                  className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-primary/40 focus:border-primary"
                />
              </div>
            </div>
            
            <div>
              <label className="text-xs text-gray-600">Start Date</label>
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange("start_date", e.target.value)}
                className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-primary/40 focus:border-primary"
              />
            </div>
            
            <div>
              <label className="text-xs text-gray-600">End Date</label>
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange("end_date", e.target.value)}
                className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-primary/40 focus:border-primary"
              />
            </div>
          </div>
        </div>
      )}

      {/* Filter Toggle Button and Sort */}
      <div className="px-3 py-2 border-b border-gray-200 space-y-2">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
          >
            <Filter size={14} />
            Filters
          </button>
          <span className="text-xs text-gray-500">
            {filteredCustomers.length} customers
          </span>
        </div>
        
        {/* Sort and Export All */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 flex-1">
            <ArrowUpDown size={12} className="text-gray-500" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-xs border border-gray-200 rounded px-2 py-1 focus:ring-1 focus:ring-primary/40 focus:border-primary"
            >
              <option value="total_score">Total Score</option>
              <option value="avg_score">Avg Score</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === "desc" ? "asc" : "desc")}
              className="text-xs text-gray-600 hover:text-gray-900 px-1"
              title={`Sort ${sortOrder === "desc" ? "Ascending" : "Descending"}`}
            >
              {sortOrder === "desc" ? "↓" : "↑"}
            </button>
          </div>
          <button
            onClick={handleExportAll}
            disabled={exportingAll || filteredCustomers.length === 0}
            className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 disabled:text-gray-400 disabled:cursor-not-allowed"
            title="Export all engaged customers"
          >
            {exportingAll ? (
              <>
                <Loader2 size={12} className="animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download size={12} />
                Export All
              </>
            )}
          </button>
        </div>
      </div>

      {/* Customer List */}
      <div className="flex-1 overflow-y-auto scrollbar-hide">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={30} className="animate-spin text-primary" />
          </div>
        ) : filteredCustomers.length === 0 ? (
          <div className="text-center py-12 px-4">
            <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">
              {searchQuery ? "No engaged customers found" : "No engaged customers yet"}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredCustomers.map((customer) => {
              const isSelected = selectedChat?.customer_phone === customer.customer_number &&
                selectedChat?.phone_number_id === customer.phone_id;
              
              return (
                <motion.div
                  key={`${customer.customer_number}_${customer.phone_id}`}
                  onClick={() => handleCustomerSelect(customer)}
                  className={`
                    p-3 cursor-pointer transition-colors duration-200
                    ${isSelected ? "bg-primary/5" : "hover:bg-gray-50"}
                  `}
                  whileHover={{ backgroundColor: "rgba(0,0,0,0.02)" }}
                >
                  <div className="flex items-start gap-2.5">
                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                        <TrendingUp size={18} className="text-green-600" />
                      </div>
                    </div>

                    {/* Customer Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">
                          {customer.customer_name || customer.customer_number}
                        </h3>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExport(customer);
                          }}
                          disabled={exporting === customer.customer_number}
                          className="flex-shrink-0 ml-2 p-1 text-gray-400 hover:text-primary transition"
                          title="Export chat"
                        >
                          {exporting === customer.customer_number ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <Download size={14} />
                          )}
                        </button>
                      </div>
                      
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold text-green-600">
                          Score: {customer.total_score}
                        </span>
                        <span className="text-xs text-gray-500">
                          Avg: {customer.avg_score?.toFixed(1) || "0.0"}
                        </span>
                        <span className="text-xs text-gray-500">
                          ({customer.total_interactions} interactions)
                        </span>
                        {customer.unread_count > 0 && (
                          <span className="flex-shrink-0 bg-primary text-white text-[10px] font-semibold rounded-full w-5 h-5 flex items-center justify-center">
                            {customer.unread_count}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-600 truncate">
                          {customer.last_message?.message_text || "No messages"}
                        </p>
                        <span className="text-[10px] text-gray-500 flex-shrink-0 ml-2">
                          Started: {formatTimestampForDisplay(customer.engagement_started_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default EngagedCustomerList;

